import React, { useEffect, useState } from 'react';
import { FileText, User, Ticket, Clock, PhoneIncoming, PhoneOutgoing, Phone } from 'lucide-react';
import { QueuedCall } from '../../types/call-queue.types';

interface CallQueueItemProps {
  call: QueuedCall;
  isSelected: boolean;
  onClick: () => void;
  /** Компактный режим для отображения в списках супервайзора */
  compact?: boolean;
  /** Только для чтения - отключает интерактивность */
  readOnly?: boolean;
}

/**
 * Форматирование телефона для отображения
 */
function formatPhone(phone: string): string {
  if (!phone) return '';
  // Если уже отформатирован
  if (phone.includes(' ') || phone.includes('-')) return phone;

  // Форматируем казахстанский номер
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  return phone;
}

/**
 * Форматирование времени ожидания
 */
function formatElapsedTime(createdAt: number): string {
  const elapsed = Math.floor((Date.now() - createdAt) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Получение иконки для типа subject
 */
function getSubjectIcon(subjectType?: string) {
  switch (subjectType) {
    case 'agreement':
      return <FileText size={14} />;
    case 'lead':
      return <User size={14} />;
    case 'ticket':
      return <Ticket size={14} />;
    default:
      return null;
  }
}

/**
 * Получение названия типа subject
 */
function getSubjectLabel(subjectType?: string, subjectId?: string | number): string {
  switch (subjectType) {
    case 'agreement':
      return `Договор #${subjectId}`;
    case 'lead':
      return `Лид #${subjectId}`;
    case 'ticket':
      return `Тикет #${subjectId}`;
    default:
      return '';
  }
}

/**
 * Получение названия направления
 */
function getDirectionLabel(direction: string | null): string {
  switch (direction) {
    case 'inbound':
      return 'Вх';
    case 'outbound':
      return 'Исх';
    case 'dialer':
      return 'Авто';
    default:
      return '';
  }
}

const CallQueueItem: React.FC<CallQueueItemProps> = ({ call, isSelected, onClick, compact, readOnly }) => {
  const [elapsed, setElapsed] = useState(() => formatElapsedTime(call.createdAt));

  // Обновляем таймер каждую секунду
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(formatElapsedTime(call.createdAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [call.createdAt]);

  const itemClasses = [
    'call-queue-item',
    isSelected && 'call-queue-item--selected',
    call.status === 'active' && 'call-queue-item--active',
    call.priority === 'urgent' && 'call-queue-item--urgent',
    call.priority === 'overdue' && 'call-queue-item--overdue',
    compact && 'call-queue-item--compact',
    readOnly && 'call-queue-item--readonly',
  ]
    .filter(Boolean)
    .join(' ');

  const indicatorClasses = [
    'call-queue-item__indicator',
    call.status === 'active' && 'call-queue-item__indicator--active',
    call.status === 'pending_result' && 'call-queue-item__indicator--pending',
  ]
    .filter(Boolean)
    .join(' ');

  const timerClasses = [
    'call-queue-item__timer',
    call.priority === 'urgent' && 'call-queue-item__timer--urgent',
    call.priority === 'overdue' && 'call-queue-item__timer--overdue',
  ]
    .filter(Boolean)
    .join(' ');

  const directionClasses = [
    'call-queue-item__direction',
    call.direction === 'inbound' && 'call-queue-item__direction--inbound',
    call.direction === 'outbound' && 'call-queue-item__direction--outbound',
  ]
    .filter(Boolean)
    .join(' ');

  const DirectionIcon =
    call.direction === 'inbound' ? PhoneIncoming : call.direction === 'outbound' ? PhoneOutgoing : Phone;

  const handleClick = readOnly ? undefined : onClick;

  return (
    <div
      className={itemClasses}
      onClick={handleClick}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? -1 : 0}
    >
      <span className={indicatorClasses} />

      <div className="call-queue-item__content">
        <div className="call-queue-item__phone">{formatPhone(call.phone)}</div>

        {call.subjectType && (
          <div className="call-queue-item__subject">
            <span className="call-queue-item__subject-icon">{getSubjectIcon(call.subjectType)}</span>
            <span>{getSubjectLabel(call.subjectType, call.subjectId)}</span>
          </div>
        )}

        {call.customerFullName && <div className="call-queue-item__customer">{call.customerFullName}</div>}

        <div className="call-queue-item__meta">
          <span className={timerClasses}>
            <Clock size={12} />
            {elapsed}
          </span>

          <span className={directionClasses}>
            <DirectionIcon size={10} />
            {getDirectionLabel(call.direction)}
          </span>
        </div>
      </div>

      {!call.seen && <span className="call-queue-item__unseen-dot" />}
    </div>
  );
};

export default CallQueueItem;
